import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiZap,
  FiX,
  FiPaperclip,
  FiImage,
  FiVideo,
  FiFile,
  FiExternalLink,
  FiSettings,
  FiUploadCloud,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiSave,
  FiUsers,
  FiMic,
  FiArrowUp,
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiMaximize2,
  FiRotateCcw,
  FiLayers,
  FiLoader,
} from 'react-icons/fi';
import { FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { contentService, automationSourceService, pipelineService, voiceService, whatsAppConfigService } from '../services/apiService';
import { useAuthStore } from '../utils/store';
import { useT } from '../utils/i18n';
import { formatISTDateTime, formatISTDateTimeSeconds, istLocalToISO } from '../utils/dateUtils';
import { SearchableSelect } from '../components/Common/SearchableSelect';
import { canPoll } from '../utils/requestGovernor';

// ─── Tab definitions — currently just Pipeline; more automation tabs land here later ──
const TABS = [
  { id: 'pipeline', labelKey: 'automation.tab.pipeline' },
];

// Maps an output source key to the content-generation prompt used for Step 1.
// Only YouTube is wired up today — new destinations register a prompt here
// (in backend/src/ai/prompts/PromptRegistry.js) and a mapping entry here.
const DESTINATION_CONTENT_TYPE = {
  youtube: 'automationPipelineYoutube',
};

const SOURCE_ICONS = {
  whatsapp: FaWhatsapp,
  youtube: FaYoutube,
};

const STATUS_BADGE = {
  collecting: 'badge-warning',
  received:   'badge-primary',
  generating: 'badge-warning',
  generated:  'badge-primary',
  publishing: 'badge-warning',
  published:  'badge-success',
  failed:     'badge-danger',
};

function attachmentIcon(type) {
  if (type === 'image') return FiImage;
  if (type === 'video') return FiVideo;
  return FiFile;
}

// Parses the raw AI output into structured fields for the review/edit form.
// Tolerant of header wording an edited "Text prompt" override might produce
// (e.g. "TITLE:" instead of "VIDEO_TITLE:", "VIDEO TAGS:" with a space
// instead of an underscore) — confirmed via a real generation that an admin
// prompt edit had drifted to exactly this, and a byte-exact-only parser
// silently blanked the whole modal with no way to tell why. Finds each
// header's own position in the text (not a fixed "look for the next literal
// label" lookahead), so it's wording- and reorder-tolerant: whichever of the
// four headers are actually present get sliced out between their real
// positions. Thumbnail text is NOT part of this output — it has its own
// dedicated prompt/generation call (see runGenerate in GenerateModal).
function parsePipelineOutput(raw) {
  const text = raw || '';

  const HEADER_PATTERNS = [
    { key: 'title',       re: /^[ \t]*(?:VIDEO[_\s]*)?TITLE[ \t]*:/im },
    { key: 'description', re: /^[ \t]*(?:VIDEO[_\s]*)?DESCRIPTION[ \t]*:/im },
    { key: 'hashtags',    re: /^[ \t]*HASH[_\s]*TAGS[ \t]*:/im },
    { key: 'videoTags',   re: /^[ \t]*VIDEO[_\s]*TAGS[ \t]*:/im },
  ];

  const found = HEADER_PATTERNS
    .map(({ key, re }) => {
      const m = re.exec(text);
      return m ? { key, start: m.index, contentStart: m.index + m[0].length } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);

  const sections = {};
  for (let i = 0; i < found.length; i++) {
    const end = i + 1 < found.length ? found[i + 1].start : text.length;
    sections[found[i].key] = text.slice(found[i].contentStart, end).trim();
  }

  const title = sections.title || '';
  const description = sections.description || '';
  const hashtagsRaw = sections.hashtags || '';
  const videoTagsRaw = sections.videoTags || '';

  return {
    title,
    description,
    hashtags: hashtagsRaw.split(/\s+/).filter(Boolean),
    // Comma-separated per the prompt's own instructions, but tolerant of a
    // drifted override that lists them one per line instead — same reasoning
    // as the header tolerance above.
    videoTags: videoTagsRaw.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean),
    rawOutput: text,
  };
}

// ─── Page ──────────────────────────────────────────────────────────────────

export const Automation = () => {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // Managers get exactly one admin-adjacent privilege: configuring pipeline sources.
  const canConfigureSources = isAdmin || user?.role === 'manager';

  const [activeTab] = useState('pipeline');

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('automation.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('automation.desc')}</p>
        </div>
        {canConfigureSources && (
          <button
            onClick={() => navigate('/settings/automation-sources')}
            className="btn btn-ghost flex items-center gap-1.5 text-sm flex-shrink-0"
          >
            <FiSettings size={14} />
            {t('automation.configureSources')}
          </button>
        )}
      </div>

      {/* Tab bar — single tab today, kept for consistency + future growth */}
      <div className="hidden md:flex border-b border-gray-200 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 bg-primary-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {activeTab === 'pipeline' && <PipelineTab t={t} isAdmin={isAdmin} />}
    </div>
  );
};

// ─── Pipeline tab ─────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const emptyDateRange = { receivedFrom: '', receivedTo: '', publishedFrom: '', publishedTo: '' };

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.add(i);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
}

const PipelineTab = ({ t, isAdmin }) => {
  const [items, setItems]           = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [groupFilter, setGroupFilter]   = useState(''); // '' = all, '__direct__' = non-group only, else a monitored group name
  const [nameInput, setNameInput]     = useState('');   // sender name — phrase match, debounced
  const [numberInput, setNumberInput] = useState('');   // sender number — phrase match, debounced
  const [nameFilter, setNameFilter]     = useState('');
  const [numberFilter, setNumberFilter] = useState('');
  const [dateRange, setDateRange]       = useState(emptyDateRange);
  const [outputSources, setOutputSources] = useState([]);
  const [inputSources, setInputSources]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [activeItem, setActiveItem] = useState(null); // item open in the Execute/Publish modal
  const [viewItem, setViewItem]     = useState(null); // item open in the View/Edit detail modal

  // Group/Contact filter options come straight from the WhatsApp source's
  // admin-configured monitored list — no separate endpoint needed.
  const whatsappMonitored = inputSources.find((s) => s.key === 'whatsapp')?.config?.monitored || { contacts: [], groups: [] };

  const fetchSources = useCallback(async () => {
    try {
      const [outputs, inputs] = await Promise.all([
        automationSourceService.listSources('output'),
        automationSourceService.listSources('input'),
      ]);
      setOutputSources(outputs || []);
      setInputSources(inputs || []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  }, []);

  // silent=true for background-triggered refreshes (SSE push, fallback poll)
  // — the user didn't ask for these, so a transient failure (rate limit or
  // otherwise) shouldn't surface an alarming toast; it'll just retry next
  // cycle. User-triggered calls (initial load, pagination, filters) keep
  // normal error visibility.
  const fetchItems = useCallback(async (page = 1, { silent = false } = {}) => {
    setLoading(true);
    try {
      const filters = { page, limit: PAGE_SIZE };
      if (statusFilter) filters.status = statusFilter;
      if (sourceFilter) filters.source = sourceFilter;
      if (groupFilter) filters.groupName = groupFilter;
      if (nameFilter) filters.senderName = nameFilter;
      if (numberFilter) filters.senderNumber = numberFilter;
      if (dateRange.receivedFrom) filters.receivedFrom = istLocalToISO(dateRange.receivedFrom);
      if (dateRange.receivedTo) filters.receivedTo = istLocalToISO(dateRange.receivedTo);
      if (dateRange.publishedFrom) filters.publishedFrom = istLocalToISO(dateRange.publishedFrom);
      if (dateRange.publishedTo) filters.publishedTo = istLocalToISO(dateRange.publishedTo);
      const result = await pipelineService.listItems(filters, { silent });
      setItems(result.items || []);
      setPagination(result.pagination || { page: 1, pages: 1, total: 0 });
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, groupFilter, nameFilter, numberFilter, dateRange]);

  useEffect(() => { fetchSources(); }, [fetchSources]);
  useEffect(() => { fetchItems(1); }, [fetchItems]);

  // Real-time push: the backend emits an SSE event the instant a WhatsApp
  // message is captured live or recovered by the catch-up sync (an ~8s safety
  // net server-side — see WhatsAppConnectionService/PipelineController.
  // streamEvents), so a captured message shows up here within a couple of
  // seconds instead of waiting on a poll interval. This is the primary
  // delivery mechanism now; polling below is just a fallback.
  useEffect(() => {
    const es = new EventSource(pipelineService.getEventsUrl());
    es.onmessage = () => {
      if (document.visibilityState !== 'visible') return;
      if (activeItem || viewItem) return;
      fetchItems(pagination.page, { silent: true });
    };
    // EventSource reconnects on its own after a transient drop (paced by the
    // server's `retry: 10000` directive) — nothing else to do here on error.
    return () => es.close();
  }, [fetchItems, pagination.page, activeItem, viewItem]);

  // Safety net only, now that SSE carries the real-time load — lengthened
  // from the old 20s polling interval since it no longer needs to be the
  // primary delivery path. Covers the case where the SSE connection drops
  // without the browser's EventSource noticing (rare, but happens behind some
  // proxies). Every /api/* call shares one IP-wide rate-limit budget (100
  // req / 15 min by default), and over-eager polling has caused real 429
  // outages earlier in this project, so this stays deliberately infrequent.
  const AUTO_REFRESH_MS = 90000;
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (activeItem || viewItem) return;
      if (!canPoll()) return; // client-side budget tight — skip, retry next tick
      fetchItems(pagination.page, { silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchItems, pagination.page, activeItem, viewItem]);

  // Debounce the free-text Name/Number filters so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setNameFilter(nameInput.trim()), 400);
    return () => clearTimeout(id);
  }, [nameInput]);
  useEffect(() => {
    const id = setTimeout(() => setNumberFilter(numberInput.trim()), 400);
    return () => clearTimeout(id);
  }, [numberInput]);

  const handleClearFilters = () => {
    setStatusFilter('');
    setSourceFilter('');
    setGroupFilter('');
    setNameInput('');
    setNumberInput('');
    setDateRange(emptyDateRange);
  };

  const hasActiveFilters = statusFilter || sourceFilter || groupFilter || nameFilter || numberFilter
    || dateRange.receivedFrom || dateRange.receivedTo || dateRange.publishedFrom || dateRange.publishedTo;

  // The actual "messages missing" symptom is almost always the WhatsApp
  // session having silently dropped — the table refresh alone can't fix
  // that, since there's nothing new to fetch. So a manual refresh also pokes
  // the WhatsApp connection: if it's already connected this is a fast no-op
  // (initClient() short-circuits immediately — see WhatsAppConnectionService),
  // and if it dropped, the saved LocalAuth session lets it reconnect silently
  // without a QR the vast majority of the time (only genuinely needed again
  // if the phone itself unlinked the session). Once reconnected, the
  // existing catch-up sync + SSE push (see PipelineController.streamEvents)
  // take over automatically — no need to click refresh a second time.
  // Fire-and-forget and fully silent on failure: editors don't have
  // permission to reconnect WhatsApp (admin/manager-only, same as every
  // other source-configuration action in this app) and would otherwise see a
  // confusing 403 for an action they didn't consciously take.
  const handleManualRefresh = () => {
    whatsAppConfigService.connect().catch(() => {});
    fetchItems(pagination.page);
  };

  const [backfilling, setBackfilling] = useState(false);
  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const result = await pipelineService.backfillMonitoredFields();
      toast.success(t('pipeline.backfillResult', result));
      fetchItems(pagination.page);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setBackfilling(false);
    }
  };

  const handleDestinationChange = async (item, destination) => {
    try {
      const updated = await pipelineService.updateItem(item._id, { destination });
      setItems((prev) => prev.map((i) => (i._id === item._id ? updated : i)));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const syncItem = (updated) => {
    setItems((prev) => prev.map((i) => (i._id === updated._id ? updated : i)));
  };

  const handleItemSaved = (updated) => {
    syncItem(updated);
    setActiveItem(updated);
  };

  const handleViewItemUpdated = (updated) => {
    syncItem(updated);
    setViewItem(updated);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-3 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('pipeline.filterStatus')}</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input text-sm py-1.5 w-auto">
              <option value="">{t('pipeline.filterAllStatus')}</option>
              {['collecting', 'received', 'generating', 'generated', 'publishing', 'published', 'failed'].map((s) => (
                <option key={s} value={s}>{t(`pipeline.status.${s}`)}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('pipeline.filterSource')}</span>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="input text-sm py-1.5 w-auto">
              <option value="">{t('pipeline.filterAllSources')}</option>
              {inputSources.map((s) => (
                <option key={s.key} value={s.key}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('pipeline.filterGroup')}</span>
            <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="input text-sm py-1.5 w-auto">
              <option value="">{t('pipeline.filterAllGroups')}</option>
              <option value="__direct__">{t('pipeline.filterDirect')}</option>
              {whatsappMonitored.groups.map((g) => (
                <option key={g.name} value={g.name}>{g.label || g.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('pipeline.filterName')}</span>
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder={t('pipeline.filterNamePlaceholder')}
              className="input text-sm py-1.5 w-36"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('pipeline.filterNumber')}</span>
            <input
              value={numberInput}
              onChange={(e) => setNumberInput(e.target.value)}
              placeholder={t('pipeline.filterNumberPlaceholder')}
              className="input text-sm py-1.5 w-32 font-mono"
            />
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            {hasActiveFilters && (
              <button onClick={handleClearFilters} className="btn btn-ghost text-xs text-gray-500">
                {t('pipeline.clearFilters')}
              </button>
            )}
            {isAdmin && (
              <button
                onClick={handleBackfill}
                disabled={backfilling}
                title={t('pipeline.backfillHint')}
                className="btn btn-ghost text-xs text-gray-500 disabled:opacity-50"
              >
                {backfilling ? t('pipeline.backfilling') : t('pipeline.backfill')}
              </button>
            )}
            <button onClick={handleManualRefresh} className="btn btn-ghost text-sm flex items-center gap-1.5">
              <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="flex items-end gap-2 flex-wrap border-t border-gray-100 pt-2.5">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">{t('pipeline.filterReceivedFrom')}</label>
            <input
              type="datetime-local"
              value={dateRange.receivedFrom}
              onChange={(e) => setDateRange((d) => ({ ...d, receivedFrom: e.target.value }))}
              className="input text-sm py-1.5"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">{t('pipeline.filterReceivedTo')}</label>
            <input
              type="datetime-local"
              value={dateRange.receivedTo}
              onChange={(e) => setDateRange((d) => ({ ...d, receivedTo: e.target.value }))}
              className="input text-sm py-1.5"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">{t('pipeline.filterPublishedFrom')}</label>
            <input
              type="datetime-local"
              value={dateRange.publishedFrom}
              onChange={(e) => setDateRange((d) => ({ ...d, publishedFrom: e.target.value }))}
              className="input text-sm py-1.5"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">{t('pipeline.filterPublishedTo')}</label>
            <input
              type="datetime-local"
              value={dateRange.publishedTo}
              onChange={(e) => setDateRange((d) => ({ ...d, publishedTo: e.target.value }))}
              className="input text-sm py-1.5"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.source')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.receivedDatetime')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.receivedFrom')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.text')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.attachments')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.destination')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.status')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.publishedDatetime')}</th>
                <th className="py-3 px-4 font-semibold text-gray-900 text-xs uppercase tracking-wider">{t('pipeline.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const SourceIcon = SOURCE_ICONS[item.source] || FiPaperclip;
                const canEdit = item.status === 'collecting' || item.status === 'received' || item.status === 'failed';
                return (
                  <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span title={item.source} className="inline-flex">
                        <SourceIcon size={16} className={item.source === 'whatsapp' ? 'text-[#25D366]' : 'text-gray-400'} />
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      {formatISTDateTimeSeconds(item.receivedAt || item.createdAt)}
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      {item.groupName || item.senderName || item.senderNumber ? (
                        <div className="space-y-0.5 text-xs">
                          {item.groupName && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <FiUsers size={10} className="flex-shrink-0" />
                              <span className="truncate max-w-[160px]">{item.groupName}</span>
                            </div>
                          )}
                          {item.senderName && (
                            <div className="font-medium text-gray-800 truncate max-w-[160px]">{item.senderName}</div>
                          )}
                          {item.senderNumber && item.senderNumber !== 'unknown' && (
                            <div className="text-gray-400 font-mono">+{item.senderNumber}</div>
                          )}
                        </div>
                      ) : (
                        item.receivedFrom || '—'
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-700 max-w-xs">
                      <p className="line-clamp-2">{item.text || '—'}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-500">
                      <div className="flex flex-col gap-1">
                        {item.attachments?.length ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <FiPaperclip size={12} />
                            {t('pipeline.attachmentsCount', { count: item.attachments.length })}
                          </span>
                        ) : !item.pendingAttachmentCount ? (
                          <span className="text-gray-300">{t('pipeline.noAttachments')}</span>
                        ) : null}
                        {item.pendingAttachmentCount > 0 && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-primary-600">
                            <span className="w-2.5 h-2.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            {t('pipeline.capturingAttachment')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <SearchableSelect
                        value={item.destination || ''}
                        onChange={(key) => handleDestinationChange(item, key)}
                        options={outputSources.map((s) => ({ key: s.key, label: s.name }))}
                        placeholder={t('pipeline.selectDestination')}
                        disabled={!canEdit}
                        className="w-full min-w-[9rem]"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <span className={`badge ${STATUS_BADGE[item.status] || 'badge-primary'} text-xs`}>
                        {t(`pipeline.status.${item.status}`)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      {item.publishedAt ? formatISTDateTimeSeconds(item.publishedAt) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setViewItem(item)}
                          title={t('pipeline.viewEditMessage')}
                          className="btn btn-ghost btn-sm flex items-center justify-center px-2"
                        >
                          <FiEye size={14} />
                        </button>
                        <button
                          onClick={() => setActiveItem(item)}
                          disabled={!item.destination || item.status === 'collecting'}
                          title={
                            item.status === 'collecting'
                              ? t('pipeline.stillCollecting')
                              : !item.destination ? t('pipeline.modal.selectDestinationFirst') : ''
                          }
                          className="btn btn-primary btn-sm flex items-center gap-1.5 disabled:opacity-40"
                        >
                          <FiZap size={12} />
                          {item.status === 'received' || item.status === 'failed' ? t('pipeline.execute') : t('pipeline.viewEdit')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-sm text-gray-400">
                    {t('pipeline.noItems')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500 flex-wrap gap-2">
            <span>
              {t('pipeline.pagination.showing', {
                from: (pagination.page - 1) * PAGE_SIZE + 1,
                to: Math.min(pagination.page * PAGE_SIZE, pagination.total),
                total: pagination.total,
              })}
            </span>
            {pagination.pages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => fetchItems(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="btn btn-ghost btn-sm disabled:opacity-40"
                >
                  {t('pipeline.pagination.prev')}
                </button>
                {getPageNumbers(pagination.page, pagination.pages).map((p, i) => (
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-1.5 text-gray-300">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => fetchItems(p)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                        p === pagination.page ? 'bg-primary-500 text-white' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  )
                ))}
                <button
                  onClick={() => fetchItems(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="btn btn-ghost btn-sm disabled:opacity-40"
                >
                  {t('pipeline.pagination.next')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {activeItem && (() => {
        // Resolved once here (outputSources already has every destination's
        // full doc, provider included) and passed down as plain props — the
        // modal itself never needs to know about multiple channels of the
        // same provider, only "which provider is this specific destination."
        const destinationSource = outputSources.find((s) => s.key === activeItem.destination);
        const provider = destinationSource?.provider || activeItem.destination;
        return (
          <GenerateModal
            item={activeItem}
            destinationName={destinationSource?.name || activeItem.destination}
            contentType={DESTINATION_CONTENT_TYPE[provider]}
            provider={provider}
            defaultPrivacyStatus={destinationSource?.config?.defaultPrivacyStatus || 'unlisted'}
            onClose={() => setActiveItem(null)}
            onSaved={handleItemSaved}
            t={t}
          />
        );
      })()}

      {viewItem && (
        <DetailModal
          item={viewItem}
          onClose={() => setViewItem(null)}
          onUpdated={handleViewItemUpdated}
          t={t}
        />
      )}
    </div>
  );
};

// Admin-only deep link straight into the Prompt Editor for a given
// contentType — same navigation used elsewhere in the app (see GenPage.jsx's
// "Prompt used" section) so editing a pipeline prompt lands exactly where
// editing any other content type's prompt does. Self-contained (checks the
// role itself) so call sites don't need to thread isAdmin through.
const PromptEditLink = ({ contentType, t }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  if (!isAdmin) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(`/settings/prompt-editor?type=${contentType}`)}
      title={t('pipeline.modal.editPromptHint')}
      className="text-[11px] text-primary-600 hover:underline flex items-center gap-1 flex-shrink-0"
    >
      <FiEdit3 size={10} />
      {t('pipeline.modal.editPrompt')}
    </button>
  );
};

// ─── Generate / Publish modal (the 2-step execute flow) ────────────────────

const GenerateModal = ({ item, destinationName, contentType, provider, defaultPrivacyStatus, onClose, onSaved, t }) => {
  // Every "YouTube only" branch below tests this, not item.destination itself —
  // several destinations (several channels) can all share provider 'youtube'.
  const isYouTubeDestination = provider === 'youtube';

  const [fields, setFields] = useState({
    title:         item.generatedContent?.title || '',
    description:   item.generatedContent?.description || '',
    hashtags:      (item.generatedContent?.hashtags || []).join(' '),
    videoTags:     (item.generatedContent?.videoTags || []).join(', '),
    thumbnailText: item.generatedContent?.thumbnailText || '',
    rawOutput:     item.generatedContent?.rawOutput || '',
  });
  // Already-published items show whatever privacy status YouTube actually
  // confirmed (see publishedResult.privacyStatus, set from the real API
  // response) rather than today's Settings default, which may have changed
  // since this video was published.
  const [privacyStatus, setPrivacyStatus] = useState(
    item.publishedResult?.privacyStatus || defaultPrivacyStatus || 'unlisted'
  );
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError]           = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(
    item.selectedAttachmentIndex ?? (item.attachments?.length === 1 ? 0 : null)
  );

  // ── Multi-video merge (Input Videos → Output Videos) ───────────────────────
  // Draft sequence numbers keyed by filename, seeded from whatever's already
  // persisted on the item (attachment.sequenceOrder) — local until "Merge"
  // is clicked, which both saves the sequence and triggers the merge in one
  // action (nothing else ever needs the sequence persisted on its own).
  const [sequenceDraft, setSequenceDraft] = useState(() => {
    const draft = {};
    for (const att of item.attachments || []) {
      if (att.type === 'video' && att.sequenceOrder != null) draft[att.filename] = att.sequenceOrder;
    }
    return draft;
  });
  const [merging, setMerging] = useState(false);
  const [selectedMergeOutput, setSelectedMergeOutput] = useState(item.selectedMergeOutputFilename || '');
  const videoAttachments = (item.attachments || []).filter((a) => a.type === 'video');
  const nonVideoAttachments = (item.attachments || []).filter((a) => a.type !== 'video');

  // The video that voice-over/thumbnail generation (and publish) should
  // actually operate on — a merge output takes priority when one's selected,
  // same preference publish already applies. Voice-over and thumbnail
  // generation previously only ever looked at item.attachments[selectedAttachment],
  // which is never set once 2+ videos exist (that index only comes from the
  // single-video picker, hidden once there's something to merge) — so for
  // any item with a merged video, clicking Generate silently did nothing at
  // all: this fell through to null and bailed out before even making a
  // request. Confirmed by a real report ("nothing happens").
  const activeVideoFilename = selectedMergeOutput
    || (selectedAttachment !== null && selectedAttachment !== undefined ? item.attachments[selectedAttachment]?.filename : null);

  // ── Voice-over (YouTube only) ──────────────────────────────────────────────
  // Always 'yes' or 'no' (never a third "undecided" state) so the toggle is
  // always visible and switchable in either direction, regardless of whether a
  // previous generate attempt is idle/failed/completed.
  const [voiceOverChoice, setVoiceOverChoice] = useState(item.voiceOver?.requested ? 'yes' : 'no');
  const [voiceOptions, setVoiceOptions]       = useState([]);
  const [selectedVoice, setSelectedVoice]     = useState('');
  const [startingVoiceOver, setStartingVoiceOver] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const jobPollRef = useRef(null);

  const voiceOverStatus = item.voiceOver?.status || 'idle';
  const voiceOverInProgress = ['generating_script', 'generating_audio', 'merging'].includes(voiceOverStatus);

  // Merge/publish are now background jobs too (see PipelineController.mergeVideos/
  // publishItem) — the initiating request returns almost instantly, and the
  // real progress lives here, polled exactly like voiceOverStatus already is.
  const mergeStatus = item.merge?.status || 'idle';
  const mergeInProgress = mergeStatus === 'merging';
  const publishStatus = item.publish?.status || 'idle';
  const publishInProgress = ['watermarking', 'uploading', 'setting_thumbnail'].includes(publishStatus);
  const anyJobInProgress = voiceOverInProgress || mergeInProgress || publishInProgress;

  const fetchVoiceOptions = useCallback(async (preserveSelection) => {
    setLoadingVoices(true);
    try {
      const [artists, elVoices] = await Promise.all([
        voiceService.listArtists(),
        voiceService.listElevenLabsVoices(),
      ]);
      const options = [
        ...(artists || []).map((a) => ({ value: `artist:${a._id}`, label: a.name })),
        // No provider name in the label — admins shouldn't see which TTS
        // vendor is behind a voice, just its name (matches the "artist"
        // voices above, which never expose a vendor either).
        ...(elVoices || []).map((v) => ({ value: `el:${v.voice_id}:${v.name}`, label: v.name })),
      ];
      setVoiceOptions(options);
      // Drop the current selection on refresh if it's no longer in the fresh
      // list — this is exactly the "voice not found" scenario: a stale ID that
      // was removed from ElevenLabs since the list was first fetched.
      setSelectedVoice((prev) => {
        if (preserveSelection && options.some((o) => o.value === prev)) return prev;
        return options[0]?.value || '';
      });
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoadingVoices(false);
    }
  }, []);

  useEffect(() => {
    if (voiceOverChoice !== 'yes' || voiceOptions.length) return;
    fetchVoiceOptions(true);
  }, [voiceOverChoice, voiceOptions.length, fetchVoiceOptions]);

  // Adaptive interval, not a fixed 3s: generation/merge/publish can all
  // legitimately run for a long time now (see each service's own timeout
  // ceilings, scaled up to support hours-long merged/narrated video — see
  // PipelineVoiceOverService, PipelineVideoMergeService), so a flat 3s poll
  // could burn hundreds of requests on a single run. Quick at first (while
  // the admin is actively watching), backs off once it's clearly a
  // longer-running attempt — combined with canPoll() below (the shared
  // client-side budget every polling loop in the app respects), this is what
  // makes "never reaches 429" a real guarantee rather than a hope.
  //
  // One poll loop covers all three job types (voice-over/merge/publish)
  // rather than three independent ones — they'd otherwise all compete for
  // the same canPoll() budget for what's really just "poll this item until
  // nothing on it is running".
  useEffect(() => {
    if (!anyJobInProgress) return undefined;
    const startedAt = Date.now();
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (canPoll()) {
        try {
          const polled = await pipelineService.getItem(item._id);
          onSaved(polled);
          if (polled.selectedAttachmentIndex !== null && polled.selectedAttachmentIndex !== undefined) {
            setSelectedAttachment(polled.selectedAttachmentIndex);
          }
        } catch (_) { /* transient — retry next tick */ }
      }
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      const nextDelay = elapsed < 30000 ? 3000 : 10000;
      jobPollRef.current = setTimeout(tick, nextDelay);
    };
    jobPollRef.current = setTimeout(tick, 3000);

    return () => {
      cancelled = true;
      clearTimeout(jobPollRef.current);
    };
  }, [anyJobInProgress, item._id]);

  // Fires the "it's actually done" side effects when the poll above detects
  // a status transition — necessary now that handleMergeVideos/handlePublish
  // only kick the job off and return almost instantly (a 202 ack), instead
  // of resolving once the real work is finished the way they used to. A
  // useRef (not state) tracks the previous status purely to detect the
  // transition edge, without triggering its own re-render.
  const prevMergeStatusRef = useRef(mergeStatus);
  useEffect(() => {
    const prev = prevMergeStatusRef.current;
    prevMergeStatusRef.current = mergeStatus;
    if (prev !== 'merging') return; // only react to a transition OUT of "merging", not the initial mount
    if (mergeStatus === 'completed') {
      // Auto-select the output that just got created — without this, the
      // admin has to notice the new card under "Output Videos" and manually
      // click its radio button before Generate (voice-over/thumbnail) will
      // do anything at all (confirmed by a real report).
      const newOutput = item.mergeOutputs?.[item.mergeOutputs.length - 1];
      if (newOutput) {
        setSelectedMergeOutput(newOutput.filename);
        pipelineService.updateItem(item._id, { selectedMergeOutputFilename: newOutput.filename }).then(onSaved).catch(() => {});
      }
      toast.success(t('pipeline.modal.mergeSuccess'));
    } else if (mergeStatus === 'failed') {
      const message = item.merge?.error || t('pipeline.modal.mergeFailed');
      setError(message);
      toast.error(message);
    }
  }, [mergeStatus]);

  const prevPublishStatusRef = useRef(publishStatus);
  useEffect(() => {
    const prev = prevPublishStatusRef.current;
    prevPublishStatusRef.current = publishStatus;
    if (!['watermarking', 'uploading', 'setting_thumbnail'].includes(prev)) return;
    if (publishStatus === 'completed') {
      if (isYouTubeDestination && item.publishedResult?.url) {
        const statusKey = { public: 'youtube.privacyPublic', unlisted: 'youtube.privacyUnlisted', private: 'youtube.privacyPrivate' }[item.publishedResult.privacyStatus];
        toast.success(t('pipeline.modal.publishSuccessYoutube', {
          url: item.publishedResult.url,
          status: statusKey ? t(statusKey) : item.publishedResult.privacyStatus,
        }));
      } else {
        toast.success(t('pipeline.modal.publishSuccess'));
      }
      onClose();
    } else if (publishStatus === 'failed') {
      const message = item.publishError || t('pipeline.modal.publishFailed');
      setError(message);
      toast.error(message);
    }
  }, [publishStatus]);

  const handleStartVoiceOver = async () => {
    if (!activeVideoFilename) {
      // Toast, not just setError — same reasoning as handleMergeVideos: the
      // error banner is above the fold and this button is well below it, so
      // a setError-only failure here is indistinguishable from the button
      // doing nothing at all (confirmed by a real report).
      const message = t('pipeline.modal.voiceOverSelectVideoFirst');
      setError(message);
      toast.error(message);
      return;
    }
    if (!selectedVoice) return;
    setStartingVoiceOver(true);
    setError('');
    try {
      const body = selectedVoice.startsWith('artist:')
        ? { attachmentFilename: activeVideoFilename, artistId: selectedVoice.slice(7) }
        : (() => {
            const parts = selectedVoice.split(':');
            return { attachmentFilename: activeVideoFilename, voiceId: parts[1], voiceName: parts.slice(2).join(':') };
          })();
      const updated = await pipelineService.startVoiceOver(item._id, body);
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setStartingVoiceOver(false);
    }
  };

  const [cancellingVoiceOver, setCancellingVoiceOver] = useState(false);

  const handleCancelVoiceOver = async () => {
    setCancellingVoiceOver(true);
    setError('');
    try {
      const updated = await pipelineService.cancelVoiceOver(item._id);
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setCancellingVoiceOver(false);
    }
  };

  const [uploadingVoiceOver, setUploadingVoiceOver] = useState(false);
  const voiceOverFileInputRef = useRef(null);

  const handleUploadVoiceOverAudio = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!activeVideoFilename) {
      const message = t('pipeline.modal.voiceOverSelectVideoFirst');
      setError(message);
      toast.error(message);
      return;
    }
    setUploadingVoiceOver(true);
    setError('');
    try {
      const updated = await pipelineService.uploadVoiceOverAudio(item._id, activeVideoFilename, file);
      onSaved(updated);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploadingVoiceOver(false);
    }
  };

  // ── Thumbnails (YouTube only) ──────────────────────────────────────────────
  const [generatingThumbnails, setGeneratingThumbnails] = useState(false);
  const [generatingAIThumbnails, setGeneratingAIThumbnails] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const thumbnailFileInputRef = useRef(null);
  const thumbnailTextDebounceRef = useRef(null);
  const lastSyncedThumbnailTextRef = useRef(item.generatedContent?.thumbnailText || '');
  // Tracks which generator produced the current candidate set ('frame' video
  // extraction, free/instant, or 'ai' image generation, costs money per call)
  // so the auto-resync-on-text-edit effect below regenerates with the *same*
  // method rather than silently swapping an AI background out for a video
  // frame (or vice versa) just because the admin tweaked the thumbnail text.
  // Inferred from the existing candidates' own `source` field (persisted on
  // the item, unlike the old approach here of tracking it only in local
  // state) when they're all the same kind — reopening an item that already
  // has frame-based candidates from an earlier session now correctly
  // resumes auto-resync-on-text-edit immediately, instead of silently doing
  // nothing until an explicit re-click of "Generate" first re-established the
  // mode. Left unknown (null) only when candidates are a mix of frame/AI (or
  // there are none at all) — genuinely ambiguous which method a text edit
  // should resync against, so still requires an explicit click, matching the
  // original caution around not guessing wrong for a paid AI regeneration.
  const [thumbnailMode, setThumbnailMode] = useState(() => {
    const sources = new Set((item.thumbnailCandidates || []).map((c) => c.source));
    if (sources.size === 1 && (sources.has('frame') || sources.has('ai'))) {
      return [...sources][0];
    }
    return null;
  });

  // ── Thumbnail text position/size controls ──────────────────────────────
  // Current values live on the item itself (item.thumbnailTextStyle) rather
  // than duplicated local state — every adjustment round-trips to the
  // backend (a cheap recomposite, not a full regenerate) and comes back via
  // onSaved, so the item is always the source of truth. Only in-flight state
  // is tracked locally, to disable the controls and avoid overlapping requests.
  const [adjustingStyle, setAdjustingStyle] = useState(false);
  const currentThumbnailStyle = item.thumbnailTextStyle || { offsetXPercent: 0, offsetYPercent: 0, fontScale: 1.3 };
  const selectedThumbnailCandidate = item.thumbnailCandidates?.find((c) => c.filename === item.selectedThumbnailFilename);
  // Only generated thumbnails have a retained background to recomposite onto —
  // an admin's own upload has nothing to reposition text against.
  const canAdjustThumbnailStyle = !!selectedThumbnailCandidate?.backgroundFilename;

  const OFFSET_STEP = 3;
  const SCALE_STEP = 0.1;

  const handleAdjustThumbnailStyle = async (delta) => {
    if (adjustingStyle) return;
    setAdjustingStyle(true);
    setError('');
    try {
      const next = {
        offsetXPercent: currentThumbnailStyle.offsetXPercent + (delta.offsetXPercent || 0),
        offsetYPercent: currentThumbnailStyle.offsetYPercent + (delta.offsetYPercent || 0),
        fontScale: Math.round((currentThumbnailStyle.fontScale + (delta.fontScale || 0)) * 100) / 100,
        thumbnailText: fields.thumbnailText,
      };
      const updated = await pipelineService.updateThumbnailStyle(item._id, next);
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setAdjustingStyle(false);
    }
  };

  const handleResetThumbnailStyle = async () => {
    if (adjustingStyle) return;
    setAdjustingStyle(true);
    setError('');
    try {
      const updated = await pipelineService.updateThumbnailStyle(item._id, {
        offsetXPercent: 0,
        offsetYPercent: 0,
        fontScale: 1.3, // 130% is the preferred default, not 100%
        thumbnailText: fields.thumbnailText,
      });
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setAdjustingStyle(false);
    }
  };

  const [maximizedThumbnail, setMaximizedThumbnail] = useState(null); // filename, or null when closed

  const handleGenerateThumbnails = async () => {
    if (!activeVideoFilename) {
      const message = t('pipeline.modal.thumbnailSelectVideoFirst');
      setError(message);
      toast.error(message);
      return;
    }
    setGeneratingThumbnails(true);
    setError('');
    try {
      // Always send the live field value, not whatever was last saved to the
      // item — this is what keeps the thumbnail text and the images in sync.
      const updated = await pipelineService.generateThumbnails(item._id, {
        attachmentFilename: activeVideoFilename,
        thumbnailText: fields.thumbnailText,
      });
      setThumbnailMode('frame');
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setGeneratingThumbnails(false);
    }
  };

  // AI-generated background image (content-relevant, not a video frame) — no
  // video attachment required. Costs real money per call (OpenAI image API),
  // so unlike the frame path this ADDS one image to the set per click rather
  // than replacing it (see aiCandidateCount/MAX_AI_THUMBNAILS below) — the
  // admin builds up to 5 options one at a time instead of a click throwing
  // away an earlier paid generation.
  const handleGenerateAIThumbnails = async () => {
    setGeneratingAIThumbnails(true);
    setError('');
    try {
      const updated = await pipelineService.generateThumbnailsAI(item._id, {
        thumbnailText: fields.thumbnailText,
      });
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setGeneratingAIThumbnails(false);
    }
  };

  const MAX_AI_THUMBNAILS = 5;
  const aiCandidateCount = item.thumbnailCandidates?.filter((c) => c.source === 'ai').length || 0;
  const aiThumbnailLimitReached = aiCandidateCount >= MAX_AI_THUMBNAILS;

  // Keeps the candidate images in sync with the Thumbnail Text field: once
  // frame-based candidates already exist, editing the text auto-regenerates
  // them (debounced, and only once the field settles) instead of requiring a
  // manual re-click — safe because frame extraction is free/instant. AI-mode
  // candidates deliberately do NOT auto-resync this way: each regeneration is
  // a real paid API call, so picking up a text edit there requires an
  // explicit "Regenerate with AI" click instead of silently re-billing on
  // every keystroke pause.
  useEffect(() => {
    if (!item.thumbnailCandidates?.length) return undefined;
    if (thumbnailMode !== 'frame') return undefined;
    if (fields.thumbnailText === lastSyncedThumbnailTextRef.current) return undefined;

    clearTimeout(thumbnailTextDebounceRef.current);
    thumbnailTextDebounceRef.current = setTimeout(() => {
      if (generatingThumbnails || uploadingThumbnail) return; // re-evaluated once the busy flag clears
      lastSyncedThumbnailTextRef.current = fields.thumbnailText;
      handleGenerateThumbnails();
    }, 900);
    return () => clearTimeout(thumbnailTextDebounceRef.current);
  }, [fields.thumbnailText, generatingThumbnails, uploadingThumbnail, thumbnailMode]);

  const handleUploadThumbnail = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingThumbnail(true);
    setError('');
    try {
      const updated = await pipelineService.uploadThumbnail(item._id, file);
      onSaved(updated);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSelectThumbnail = async (filename) => {
    try {
      const updated = await pipelineService.updateItem(item._id, { selectedThumbnailFilename: filename });
      onSaved(updated);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  // Falls back to true when we have a raw AI response but couldn't find the
  // expected VIDEO_TITLE/etc. headers in it (e.g. an admin-edited prompt
  // override that no longer follows the expected format) — previously an
  // empty-title parse meant the whole modal silently rendered nothing, with
  // no way to tell the AI call had actually succeeded. See the fallback
  // banner below, which surfaces the raw text instead of hiding it.
  const hasContent = !!fields.title || !!fields.rawOutput;

  const runGenerate = useCallback(async () => {
    if (!contentType) {
      setError(t('pipeline.modal.notSupported'));
      return;
    }
    setGenerating(true);
    setError('');
    try {
      // Thumbnail text has its own dedicated, separately admin-editable prompt
      // (automationPipelineThumbnailText) — run in parallel with the main
      // title/description/hashtags/tags generation rather than sequentially.
      // Non-fatal on its own: the rest of the YouTube package is still useful
      // even if just this one call fails, so it's caught separately instead of
      // taking down the whole generate step.
      // Captured rather than handled inline in .catch() below, so a problem
      // showing the toast itself can never turn into a second rejection that
      // Promise.all would then reject the whole call on — that previously
      // meant a failure here could take the *entire* generate step down with
      // it, wiping out the main title/description/hashtags result too.
      let thumbTextError = null;
      const [result, thumbTextResult] = await Promise.all([
        contentService.generateContent({
          contentType,
          inputContent: item.text,
          tone: 'authoritative',
          language: 'telugu',
        }),
        contentService.generateContent({
          contentType: 'automationPipelineThumbnailText',
          inputContent: item.text,
          tone: 'authoritative',
          language: 'telugu',
          enableWebSearch: false, // crafting punchy text from the given content, not researching it — same reasoning as the voice-over script
        }).catch((e) => {
          thumbTextError = e;
          return null;
        }),
      ]);
      if (thumbTextError) {
        toast.error(t('pipeline.modal.thumbnailTextGenerationFailed', { message: thumbTextError.response?.data?.message || thumbTextError.message || '' }));
      }

      const parsed = parsePipelineOutput(result?.outputs?.primaryOutput || '');
      const thumbnailText = (thumbTextResult?.outputs?.primaryOutput || '').trim();

      if (!parsed.title && parsed.rawOutput) {
        // The AI call succeeded but its response didn't contain the expected
        // VIDEO_TITLE/VIDEO_DESCRIPTION/etc. headers — most likely an
        // admin-edited prompt override (Settings → Prompts) that no longer
        // follows the format parsePipelineOutput() expects. Logged here so
        // it's diagnosable from devtools instead of just "nothing appeared";
        // the raw text is still shown to the admin below (see hasContent).
        console.error('[Pipeline] Could not find expected section headers in the AI response — showing raw output instead. Raw text:', parsed.rawOutput);
      }

      setFields({
        title: parsed.title,
        description: parsed.description,
        hashtags: parsed.hashtags.join(' '),
        videoTags: parsed.videoTags.join(', '),
        thumbnailText,
        rawOutput: parsed.rawOutput,
      });
      const updated = await pipelineService.updateItem(item._id, {
        status: 'generated',
        generatedContent: {
          title: parsed.title,
          description: parsed.description,
          hashtags: parsed.hashtags,
          videoTags: parsed.videoTags,
          thumbnailText,
          rawOutput: parsed.rawOutput,
        },
      });
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.message || e.message || t('pipeline.modal.generationFailed'));
    } finally {
      setGenerating(false);
    }
  }, [contentType, item, onSaved, t]);

  useEffect(() => {
    if (!hasContent && item.status !== 'published') runGenerate();
  }, []);

  // Assigns/clears a video's merge position. Typing the same number another
  // video already has just leaves both that way — resolved at merge time by
  // stable-sorting on the number (ties keep their attachment-list order)
  // rather than rejecting the input as the admin types.
  const handleSequenceChange = (filename, value) => {
    const n = value === '' ? null : Math.max(1, Math.round(Number(value)) || 1);
    setSequenceDraft((prev) => {
      const next = { ...prev };
      if (n === null) delete next[filename];
      else next[filename] = n;
      return next;
    });
  };

  // Every video with a sequence number gets merged, in that order (ties keep
  // list order) — as few as one video is a valid "merge" (a normalize-only
  // pass, see PipelineVideoMergeService), all the way up to every available
  // video. Persists the sequence first so it survives a page reload even if
  // the admin never actually clicks Merge again later.
  const handleMergeVideos = async () => {
    const ordered = Object.keys(sequenceDraft)
      .filter((filename) => item.attachments?.some((a) => a.filename === filename && a.type === 'video'))
      .sort((a, b) => sequenceDraft[a] - sequenceDraft[b]);
    if (!ordered.length) return;

    setMerging(true);
    setError('');
    try {
      await pipelineService.updateAttachmentSequence(item._id, ordered);
      // Kicks off the background job and returns almost instantly (202) —
      // item.merge.status is now 'merging'. The poll loop + transition
      // effect above take it from here (auto-select the new output, show
      // the success/failure toast) once it actually finishes; this call
      // itself only confirms the job *started*.
      const updated = await pipelineService.mergeVideos(item._id, ordered);
      onSaved(updated);
      toast.success(t('pipeline.modal.mergeStarted'));
    } catch (e) {
      // The Merge button sits well below the error banner (which renders at
      // the top of the modal, right under "Original Message") — scrolled out
      // of view, a setError-only failure here looks like nothing happened at
      // all (confirmed by a real report). A toast is always visible
      // regardless of scroll position; setError still runs too, for anyone
      // who does scroll up.
      const message = e.response?.data?.message || e.message || t('pipeline.modal.mergeFailed');
      setError(message);
      toast.error(message);
    } finally {
      setMerging(false);
    }
  };

  const handleSelectMergeOutput = async (filename) => {
    setSelectedMergeOutput(filename);
    try {
      const updated = await pipelineService.updateItem(item._id, { selectedMergeOutputFilename: filename });
      onSaved(updated);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const handlePublish = async () => {
    if (!selectedMergeOutput && (selectedAttachment === null || selectedAttachment === undefined)) return;
    setPublishing(true);
    setError('');
    try {
      const generatedContent = {
        title: fields.title,
        description: fields.description,
        hashtags: fields.hashtags.split(/\s+/).filter(Boolean),
        videoTags: fields.videoTags.split(',').map((s) => s.trim()).filter(Boolean),
        thumbnailText: fields.thumbnailText,
      };
      const updated = await pipelineService.publishItem(item._id, {
        // Output-video selection wins when present — the backend applies the
        // same preference (see PipelineController.publishItem).
        selectedMergeOutputFilename: selectedMergeOutput || undefined,
        selectedAttachmentIndex: selectedAttachment,
        generatedContent,
        ...(isYouTubeDestination ? { privacyStatus } : {}),
      });
      onSaved(updated);
      // YouTube publishing is now a background job (see
      // PipelineController.publishItem/PipelinePublishService) — this
      // request only confirms it *started* (item.publish.status is
      // 'watermarking'/'uploading'), and the poll loop + transition effect
      // above handle the success/failure toast and closing the modal once it
      // actually finishes. Non-YouTube destinations have no upload step and
      // still complete synchronously right here, exactly as before.
      if (isYouTubeDestination) {
        toast.success(t('pipeline.modal.publishStarted'));
      } else {
        toast.success(t('pipeline.modal.publishSuccess'));
        onClose();
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || t('pipeline.modal.publishFailed'));
    } finally {
      setPublishing(false);
    }
  };

  const alreadyPublished = item.status === 'published';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-3 py-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t('pipeline.modal.title', { destination: destinationName || item.destination })}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{item.receivedFrom} · {formatISTDateTime(item.receivedAt || item.createdAt)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><FiX size={20} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">{t('pipeline.modal.originalMessage')}</p>
            <p className="text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 whitespace-pre-wrap">{item.text || '—'}</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {generating ? (
            <div className="flex flex-col items-center justify-center gap-2.5 py-10 text-primary-600">
              <div className="w-9 h-9 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs font-semibold">{t('pipeline.modal.generating', { destination: destinationName || item.destination })}</span>
            </div>
          ) : hasContent ? (
            <>
              {!fields.title && fields.rawOutput && (
                <div className="flex items-start gap-2 text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-xl px-3 py-2.5">
                  <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('pipeline.modal.unparsedOutputWarning')}</p>
                    <pre className="mt-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">{fields.rawOutput}</pre>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">{t('pipeline.modal.generatedContentLabel')}</p>
                {contentType && <PromptEditLink contentType={contentType} t={t} />}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.modal.videoTitle')}</label>
                <input
                  value={fields.title}
                  onChange={(e) => setFields((f) => ({ ...f, title: e.target.value }))}
                  disabled={alreadyPublished}
                  className="input text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.modal.videoDescription')}</label>
                <textarea
                  value={fields.description}
                  onChange={(e) => setFields((f) => ({ ...f, description: e.target.value }))}
                  disabled={alreadyPublished}
                  rows={5}
                  className="input text-sm resize-y"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.modal.hashtags')}</label>
                <input
                  value={fields.hashtags}
                  onChange={(e) => setFields((f) => ({ ...f, hashtags: e.target.value }))}
                  disabled={alreadyPublished}
                  className="input text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.modal.videoTags')}</label>
                <input
                  value={fields.videoTags}
                  onChange={(e) => setFields((f) => ({ ...f, videoTags: e.target.value }))}
                  disabled={alreadyPublished}
                  className="input text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.modal.thumbnailText')}</label>
                  <PromptEditLink contentType="automationPipelineThumbnailText" t={t} />
                </div>
                <input
                  value={fields.thumbnailText}
                  onChange={(e) => setFields((f) => ({ ...f, thumbnailText: e.target.value }))}
                  disabled={alreadyPublished}
                  maxLength={40}
                  className="input text-sm"
                />
              </div>

              {!alreadyPublished && (
                <button onClick={runGenerate} className="btn btn-ghost btn-sm flex items-center gap-1.5 text-xs">
                  <FiRefreshCw size={12} />
                  {t('pipeline.modal.regenerate')}
                </button>
              )}

              {/* Input/Output videos — only when there are 2+ videos, i.e.
                  something actually worth sequencing/merging. Exactly one
                  video has nothing to merge — per explicit product decision,
                  that case falls through to the simple picker below instead
                  of forcing the admin through an unnecessary merge step. */}
              {videoAttachments.length > 1 && (
                <div className="space-y-3">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('pipeline.modal.inputVideos')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {videoAttachments.map((att) => (
                        <div key={att.filename} className="border border-gray-200 rounded-lg p-2 space-y-1.5">
                          <video
                            src={pipelineService.getAttachmentUrl(att.filename)}
                            controls
                            className="w-full rounded max-h-40 bg-black"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              placeholder={t('pipeline.modal.mergeOrderPlaceholder')}
                              value={sequenceDraft[att.filename] ?? ''}
                              onChange={(e) => handleSequenceChange(att.filename, e.target.value)}
                              disabled={alreadyPublished || merging || anyJobInProgress}
                              className="input text-xs w-16 py-1"
                              title={t('pipeline.modal.mergeOrderLabel')}
                            />
                            <span className="text-[11px] text-gray-400 truncate flex-1">{att.filename}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {!alreadyPublished && (
                    <button
                      onClick={handleMergeVideos}
                      disabled={merging || anyJobInProgress || Object.keys(sequenceDraft).length === 0}
                      className="btn btn-secondary btn-sm flex items-center gap-1.5 text-xs"
                    >
                      {(merging || mergeInProgress) ? <FiLoader size={12} className="animate-spin" /> : <FiLayers size={12} />}
                      {(merging || mergeInProgress)
                        ? t('pipeline.modal.merging')
                        : t('pipeline.modal.mergeSelectedVideos', { count: Object.keys(sequenceDraft).length })}
                    </button>
                  )}

                  {/* Background job progress — merge/publish can now run for a
                      long time (up to a few hours for a merge, more for a
                      large YouTube upload), tracked via item.merge.status/
                      item.publish.status and polled exactly like voice-over
                      already is above. */}
                  {mergeInProgress && (
                    <div className="flex items-center gap-2.5 text-primary-600 py-1">
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      <span className="text-xs font-medium">{t('pipeline.modal.merging')}</span>
                    </div>
                  )}

                  {item.mergeOutputs?.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('pipeline.modal.outputVideos')}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {item.mergeOutputs.map((out) => {
                          const selected = selectedMergeOutput === out.filename;
                          return (
                            <label
                              key={out.filename}
                              className={`block border rounded-lg p-2 space-y-1.5 cursor-pointer ${
                                selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                              } ${alreadyPublished ? 'pointer-events-none opacity-60' : ''}`}
                            >
                              <video
                                src={pipelineService.getAttachmentUrl(out.filename)}
                                controls
                                className="w-full rounded max-h-40 bg-black"
                              />
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="mergeOutput"
                                  checked={selected}
                                  onChange={() => handleSelectMergeOutput(out.filename)}
                                  className="flex-shrink-0"
                                />
                                <span className="text-[11px] text-gray-500 truncate flex-1">
                                  {t('pipeline.modal.mergedFrom', { count: out.sourceFilenames?.length || 0 })}
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Simple picker — covers everything the merge UI above
                  doesn't: 0 or 1 videos (nothing to sequence/merge), plus any
                  non-video attachments regardless of video count. A lone
                  video still gets an inline preview, just without the
                  merge-order ceremony. */}
              {videoAttachments.length <= 1 && (
                <div>
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('pipeline.modal.selectAttachment')}</p>
                  {item.attachments?.length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {item.attachments.map((att, idx) => {
                        const Icon = attachmentIcon(att.type);
                        const selected = selectedAttachment === idx;
                        return (
                          <label
                            key={idx}
                            className={`flex flex-col gap-1.5 border rounded-lg px-3 py-2 cursor-pointer text-xs ${
                              selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                            } ${alreadyPublished ? 'pointer-events-none opacity-60' : ''}`}
                          >
                            {att.type === 'video' && (
                              <video src={pipelineService.getAttachmentUrl(att.filename)} controls className="w-full rounded max-h-32 bg-black" />
                            )}
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="attachment"
                                checked={selected}
                                onChange={() => setSelectedAttachment(idx)}
                                className="flex-shrink-0"
                              />
                              <Icon size={14} className="text-gray-400 flex-shrink-0" />
                              <span className="truncate flex-1">{att.filename || att.type}</span>
                              <a href={pipelineService.getAttachmentUrl(att.filename)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-primary-600 flex-shrink-0">
                                <FiExternalLink size={12} />
                              </a>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">{t('pipeline.modal.noAttachments')}</p>
                  )}
                </div>
              )}

              {/* Non-video attachments alongside a 2+-video merge set above —
                  the merge UI only lists videos, so anything else (an
                  admin's own image/file upload) still needs its own picker. */}
              {videoAttachments.length > 1 && nonVideoAttachments.length > 0 && (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {nonVideoAttachments.map((att) => {
                      const idx = item.attachments.indexOf(att);
                      const Icon = attachmentIcon(att.type);
                      const selected = selectedAttachment === idx;
                      return (
                        <label
                          key={idx}
                          className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer text-xs ${
                            selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                          } ${alreadyPublished ? 'pointer-events-none opacity-60' : ''}`}
                        >
                          <input
                            type="radio"
                            name="attachment"
                            checked={selected}
                            onChange={() => setSelectedAttachment(idx)}
                            className="flex-shrink-0"
                          />
                          <Icon size={14} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate flex-1">{att.filename || att.type}</span>
                          <a href={pipelineService.getAttachmentUrl(att.filename)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-primary-600 flex-shrink-0">
                            <FiExternalLink size={12} />
                          </a>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Privacy status (YouTube only) — pre-filled from the Settings default,
                  shown again here so the admin can see and override it right before
                  it actually posts. */}
              {isYouTubeDestination && !alreadyPublished && (
                <div className="border border-gray-200 rounded-xl p-3 space-y-1.5">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block">
                    {t('pipeline.modal.privacyStatusLabel')}
                  </label>
                  <select
                    value={privacyStatus}
                    onChange={(e) => setPrivacyStatus(e.target.value)}
                    className="input text-sm w-full sm:w-56"
                  >
                    <option value="public">{t('youtube.privacyPublic')}</option>
                    <option value="unlisted">{t('youtube.privacyUnlisted')}</option>
                    <option value="private">{t('youtube.privacyPrivate')}</option>
                  </select>
                </div>
              )}

              {/* Voice-over (YouTube only) — bordered box, Yes/No always switchable */}
              {isYouTubeDestination && !alreadyPublished && (
                <div className="border border-gray-200 rounded-xl p-3 space-y-2.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <FiMic size={12} />
                        {t('pipeline.modal.voiceOverTitle')}
                      </p>
                      <PromptEditLink contentType="automationPipelineVoiceOverScript" t={t} />
                    </div>
                    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
                      <button
                        onClick={() => setVoiceOverChoice('yes')}
                        disabled={voiceOverInProgress}
                        className={`px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                          voiceOverChoice === 'yes' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {t('pipeline.modal.voiceOverYes')}
                      </button>
                      <button
                        onClick={() => setVoiceOverChoice('no')}
                        disabled={voiceOverInProgress}
                        className={`px-3 py-1 text-xs font-medium border-l border-gray-200 transition-colors disabled:opacity-50 ${
                          voiceOverChoice === 'no' ? 'bg-gray-100 text-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {t('pipeline.modal.voiceOverNo')}
                      </button>
                    </div>
                  </div>

                  {voiceOverChoice === 'no' && (
                    <p className="text-xs text-gray-400">{t('pipeline.modal.voiceOverSkipped')}</p>
                  )}

                  {voiceOverChoice === 'yes' && (
                    <>
                      {voiceOverStatus === 'failed' && (
                        <p className="text-xs text-danger-600">{item.voiceOver?.error || t('pipeline.modal.voiceOverFailed')}</p>
                      )}

                      {voiceOverInProgress && (
                        <div className="flex items-center justify-between gap-2.5 text-primary-600 py-1">
                          <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            <span className="text-xs font-medium">{t(`pipeline.modal.voiceOverStatus.${voiceOverStatus}`)}</span>
                          </div>
                          <button
                            onClick={handleCancelVoiceOver}
                            disabled={cancellingVoiceOver}
                            className="btn btn-ghost btn-sm text-xs text-danger-600 flex items-center gap-1 disabled:opacity-50"
                          >
                            <FiX size={12} />
                            {cancellingVoiceOver ? t('pipeline.modal.voiceOverCancelling') : t('pipeline.modal.voiceOverCancel')}
                          </button>
                        </div>
                      )}

                      {voiceOverStatus === 'completed' && (
                        <div className="space-y-2">
                          <p className="text-xs text-success-700 flex items-center gap-1.5">
                            <FiCheckCircle size={12} />
                            {t('pipeline.modal.voiceOverDone', { voice: item.voiceOver?.voiceLabel || '' })}
                          </p>
                          {item.voiceOver?.mergedAttachmentFilename && (
                            <video
                              controls
                              src={pipelineService.getAttachmentUrl(item.voiceOver.mergedAttachmentFilename)}
                              className="w-full rounded-lg border border-gray-200 max-h-64"
                            />
                          )}
                        </div>
                      )}

                      {/* Voice picker + Generate/Upload — always available when not actively
                          running, so a failed or already-completed attempt can be redone with
                          a different voice or a fresh upload, not just replayed identically. */}
                      {!voiceOverInProgress && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1.5">
                            <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} className="input text-sm w-full">
                              {voiceOptions.length === 0 && <option value="">{t('pipeline.modal.voiceOverLoadingVoices')}</option>}
                              {voiceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <button
                              onClick={() => fetchVoiceOptions(false)}
                              disabled={loadingVoices}
                              title={t('pipeline.modal.voiceOverRefreshVoices')}
                              className="btn btn-ghost btn-sm flex-shrink-0 px-2 disabled:opacity-50"
                            >
                              <FiRefreshCw size={12} className={loadingVoices ? 'animate-spin' : ''} />
                            </button>
                          </div>
                          <p className="text-[11px] text-gray-400">{t('pipeline.modal.voiceOverPickVideoHint')}</p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleStartVoiceOver}
                              disabled={startingVoiceOver || uploadingVoiceOver || mergeInProgress || publishInProgress || !selectedVoice}
                              title={mergeInProgress ? t('pipeline.modal.mergeBlocksVoiceOver') : ''}
                              className="btn btn-primary btn-sm text-xs flex items-center gap-1.5 disabled:opacity-40"
                            >
                              <FiMic size={12} />
                              {startingVoiceOver
                                ? t('pipeline.modal.voiceOverStarting')
                                : voiceOverStatus === 'completed' || voiceOverStatus === 'failed'
                                  ? t('pipeline.modal.regenerate')
                                  : t('pipeline.modal.voiceOverGenerate')}
                            </button>
                            <button
                              onClick={() => voiceOverFileInputRef.current?.click()}
                              disabled={startingVoiceOver || uploadingVoiceOver || mergeInProgress || publishInProgress}
                              className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5 disabled:opacity-40"
                            >
                              <FiUploadCloud size={12} />
                              {uploadingVoiceOver ? t('pipeline.detail.uploading') : t('pipeline.modal.voiceOverUploadOwn')}
                            </button>
                            <input ref={voiceOverFileInputRef} type="file" accept="audio/*" className="hidden" onChange={handleUploadVoiceOverAudio} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Thumbnails (YouTube only) */}
              {isYouTubeDestination && !alreadyPublished && (
                <div className="border border-gray-200 rounded-xl p-3 space-y-2.5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                        {t('pipeline.modal.thumbnailsTitle')}
                      </p>
                      <PromptEditLink contentType="automationPipelineThumbnailImage" t={t} />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleGenerateThumbnails}
                        disabled={generatingThumbnails || generatingAIThumbnails}
                        title={t('pipeline.modal.thumbnailsGenerateFrameHint')}
                        className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <FiRefreshCw size={12} className={generatingThumbnails ? 'animate-spin' : ''} />
                        {generatingThumbnails
                          ? t('pipeline.modal.thumbnailsGenerating')
                          : thumbnailMode === 'frame' && item.thumbnailCandidates?.length
                            ? t('pipeline.modal.thumbnailsRegenerate')
                            : t('pipeline.modal.thumbnailsGenerateFrame')}
                      </button>
                      <button
                        onClick={handleGenerateAIThumbnails}
                        disabled={generatingThumbnails || generatingAIThumbnails || aiThumbnailLimitReached}
                        title={aiThumbnailLimitReached ? t('pipeline.modal.thumbnailsAILimitReached', { max: MAX_AI_THUMBNAILS }) : t('pipeline.modal.thumbnailsGenerateAIHint')}
                        className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <FiZap size={12} className={generatingAIThumbnails ? 'animate-spin' : ''} />
                        {generatingAIThumbnails
                          ? t('pipeline.modal.thumbnailsGenerating')
                          : aiThumbnailLimitReached
                            ? t('pipeline.modal.thumbnailsAILimitReached', { max: MAX_AI_THUMBNAILS })
                            : aiCandidateCount > 0
                              ? t('pipeline.modal.thumbnailsGenerateAIAnother', { count: aiCandidateCount, max: MAX_AI_THUMBNAILS })
                              : t('pipeline.modal.thumbnailsGenerateAI')}
                      </button>
                      <button
                        onClick={() => thumbnailFileInputRef.current?.click()}
                        disabled={uploadingThumbnail}
                        className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <FiUploadCloud size={12} />
                        {uploadingThumbnail ? t('pipeline.detail.uploading') : t('pipeline.modal.thumbnailsUploadOwn')}
                      </button>
                      <input ref={thumbnailFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadThumbnail} />
                    </div>
                  </div>

                  {generatingAIThumbnails && (
                    <div className="flex items-center gap-2 text-xs text-primary-600 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">
                      <div className="w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      {t('pipeline.modal.thumbnailsGeneratingAILong')}
                    </div>
                  )}

                  {!item.thumbnailCandidates?.length && !generatingAIThumbnails && (
                    <p className="text-xs text-gray-400">{t('pipeline.modal.thumbnailsEmpty')}</p>
                  )}

                  {item.thumbnailCandidates?.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {item.thumbnailCandidates.map((thumb) => {
                        const selected = item.selectedThumbnailFilename === thumb.filename;
                        return (
                          <label
                            key={thumb.filename}
                            className={`relative rounded-lg overflow-hidden border-2 cursor-pointer ${
                              selected ? 'border-primary-500' : 'border-transparent hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="thumbnail"
                              checked={selected}
                              onChange={() => handleSelectThumbnail(thumb.filename)}
                              className="sr-only"
                            />
                            <img
                              src={pipelineService.getAttachmentUrl(thumb.filename)}
                              alt=""
                              className="w-full aspect-video object-cover bg-gray-100"
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMaximizedThumbnail(thumb.filename); }}
                              title={t('pipeline.modal.thumbnailsViewLarge')}
                              className="absolute top-1.5 left-1.5 bg-black/55 hover:bg-black/75 text-white rounded p-1 transition-colors"
                            >
                              <FiMaximize2 size={12} />
                            </button>
                            {selected && (
                              <span className="absolute top-1.5 right-1.5 bg-primary-500 text-white rounded-full p-0.5">
                                <FiCheckCircle size={13} />
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {canAdjustThumbnailStyle && !generatingAIThumbnails && !generatingThumbnails && (
                    <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-2.5">
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-gray-500 font-medium mr-1">{t('pipeline.modal.thumbnailsPosition')}</span>
                        <button
                          type="button"
                          onClick={() => handleAdjustThumbnailStyle({ offsetYPercent: -OFFSET_STEP })}
                          disabled={adjustingStyle}
                          title={t('pipeline.modal.thumbnailsMoveUp')}
                          className="btn btn-ghost btn-sm p-1.5 disabled:opacity-40"
                        >
                          <FiArrowUp size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAdjustThumbnailStyle({ offsetYPercent: OFFSET_STEP })}
                          disabled={adjustingStyle}
                          title={t('pipeline.modal.thumbnailsMoveDown')}
                          className="btn btn-ghost btn-sm p-1.5 disabled:opacity-40"
                        >
                          <FiArrowDown size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAdjustThumbnailStyle({ offsetXPercent: -OFFSET_STEP })}
                          disabled={adjustingStyle}
                          title={t('pipeline.modal.thumbnailsMoveLeft')}
                          className="btn btn-ghost btn-sm p-1.5 disabled:opacity-40"
                        >
                          <FiArrowLeft size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAdjustThumbnailStyle({ offsetXPercent: OFFSET_STEP })}
                          disabled={adjustingStyle}
                          title={t('pipeline.modal.thumbnailsMoveRight')}
                          className="btn btn-ghost btn-sm p-1.5 disabled:opacity-40"
                        >
                          <FiArrowRight size={13} />
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-gray-500 font-medium mr-1">{t('pipeline.modal.thumbnailsSize')}</span>
                        <button
                          type="button"
                          onClick={() => handleAdjustThumbnailStyle({ fontScale: -SCALE_STEP })}
                          disabled={adjustingStyle}
                          title={t('pipeline.modal.thumbnailsSizeDecrease')}
                          className="btn btn-ghost btn-sm p-1.5 disabled:opacity-40"
                        >
                          <FiMinus size={13} />
                        </button>
                        <span className="text-xs text-gray-500 font-mono w-10 text-center">
                          {Math.round(currentThumbnailStyle.fontScale * 100)}%
                        </span>
                        <button
                          type="button"
                          onClick={() => handleAdjustThumbnailStyle({ fontScale: SCALE_STEP })}
                          disabled={adjustingStyle}
                          title={t('pipeline.modal.thumbnailsSizeIncrease')}
                          className="btn btn-ghost btn-sm p-1.5 disabled:opacity-40"
                        >
                          <FiPlus size={13} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleResetThumbnailStyle}
                        disabled={adjustingStyle}
                        className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5 disabled:opacity-40"
                      >
                        <FiRotateCcw size={12} />
                        {t('pipeline.modal.thumbnailsResetStyle')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex-shrink-0 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost btn-sm">{t('pipeline.modal.close')}</button>
          {alreadyPublished ? (
            <span className="inline-flex items-center gap-2 text-success-700 text-sm font-medium">
              <FiCheckCircle size={14} />
              {t('pipeline.status.published')}
              {item.publishedResult?.url && (
                <a href={item.publishedResult.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline flex items-center gap-1">
                  {t('pipeline.modal.viewOnYoutube')}
                  <FiExternalLink size={12} />
                </a>
              )}
            </span>
          ) : (
            <button
              onClick={handlePublish}
              disabled={
                !hasContent || generating || publishing || anyJobInProgress
                || (!selectedMergeOutput && (selectedAttachment === null || selectedAttachment === undefined))
              }
              title={
                voiceOverInProgress ? t('pipeline.modal.voiceOverBlocksPublish')
                  : mergeInProgress ? t('pipeline.modal.mergeBlocksPublish')
                    : ''
              }
              className="btn btn-primary btn-sm flex items-center gap-1.5 disabled:opacity-40"
            >
              {publishInProgress && <FiLoader size={13} className="animate-spin" />}
              {!publishInProgress && <FiUploadCloud size={13} />}
              {publishing
                ? t('pipeline.modal.publishing')
                : publishInProgress
                  ? t(`pipeline.modal.publishStep.${publishStatus}`)
                  : t('pipeline.modal.publish')}
            </button>
          )}
        </div>
      </div>

      {maximizedThumbnail && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-[60] px-4 py-6"
          onClick={(e) => { e.stopPropagation(); setMaximizedThumbnail(null); }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setMaximizedThumbnail(null); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <FiX size={26} />
          </button>
          <img
            src={pipelineService.getAttachmentUrl(maximizedThumbnail)}
            alt=""
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// ─── View / Edit detail modal — full record, editable text, attachment management ──

const DetailModal = ({ item, onClose, onUpdated, t }) => {
  const [editingText, setEditingText] = useState(false);
  const [textDraft, setTextDraft]     = useState(item.text || '');
  const [savingText, setSavingText]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [removingFilename, setRemovingFilename] = useState('');
  const fileInputRef = useRef(null);

  // WhatsApp's own sender/number resolution can be wrong (e.g. an @lid group
  // participant WhatsApp hasn't mapped to a real phone number yet) — this lets
  // an admin correct it by hand for a specific row.
  const [editingSender, setEditingSender]     = useState(false);
  const [senderNameDraft, setSenderNameDraft]   = useState(item.senderName || '');
  const [senderNumberDraft, setSenderNumberDraft] = useState(item.senderNumber && item.senderNumber !== 'unknown' ? item.senderNumber : '');
  const [savingSender, setSavingSender]       = useState(false);

  const handleSaveSender = async () => {
    setSavingSender(true);
    try {
      const updated = await pipelineService.updateItem(item._id, {
        senderName: senderNameDraft.trim(),
        senderNumber: senderNumberDraft.replace(/\D/g, ''),
      });
      onUpdated(updated);
      setEditingSender(false);
      toast.success(t('pipeline.detail.senderSaved'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSavingSender(false);
    }
  };

  const handleDiscardSender = () => {
    setSenderNameDraft(item.senderName || '');
    setSenderNumberDraft(item.senderNumber && item.senderNumber !== 'unknown' ? item.senderNumber : '');
    setEditingSender(false);
  };

  const handleSaveText = async () => {
    setSavingText(true);
    try {
      const updated = await pipelineService.updateItem(item._id, { text: textDraft });
      onUpdated(updated);
      setEditingText(false);
      toast.success(t('pipeline.detail.textSaved'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSavingText(false);
    }
  };

  const handleDiscardText = () => {
    setTextDraft(item.text || '');
    setEditingText(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const updated = await pipelineService.uploadAttachment(item._id, file);
      onUpdated(updated);
      toast.success(t('pipeline.detail.attachmentAdded'));
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (filename) => {
    if (!window.confirm(t('pipeline.detail.removeAttachmentConfirm'))) return;
    setRemovingFilename(filename);
    try {
      const updated = await pipelineService.removeAttachment(item._id, filename);
      onUpdated(updated);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setRemovingFilename('');
    }
  };

  const SourceIcon = SOURCE_ICONS[item.source] || FiPaperclip;
  const content = item.generatedContent || {};
  const hasGeneratedContent = !!content.title;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-3 py-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <SourceIcon size={20} className={`flex-shrink-0 ${item.source === 'whatsapp' ? 'text-[#25D366]' : 'text-gray-400'}`} />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 truncate">{item.receivedFrom || t('pipeline.detail.title')}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{formatISTDateTimeSeconds(item.receivedAt || item.createdAt)}</p>
            </div>
          </div>
          <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600 flex-shrink-0"><FiX size={20} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Meta grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <MetaField label={t('pipeline.col.source')} value={item.source} />
            <MetaField label={t('pipeline.filterGroup')} value={item.groupName} />
            <MetaField label={t('pipeline.col.destination')} value={item.destination} />
            <MetaField label={t('pipeline.col.status')} value={t(`pipeline.status.${item.status}`)} />
            <MetaField label={t('pipeline.col.receivedDatetime')} value={formatISTDateTimeSeconds(item.receivedAt || item.createdAt)} />
            <MetaField label={t('pipeline.col.publishedDatetime')} value={item.publishedAt ? formatISTDateTimeSeconds(item.publishedAt) : ''} />
          </div>

          {/* Sender — WhatsApp's own name/number resolution can be wrong for a
              group participant it hasn't fully identified; editable so an
              admin can correct it by hand. */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.detail.sender')}</p>
              {!editingSender && (
                <button onClick={() => setEditingSender(true)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                  <FiEdit3 size={11} /> {t('pipeline.detail.editSender')}
                </button>
              )}
            </div>
            {editingSender ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-400 uppercase tracking-widest mb-0.5">{t('pipeline.filterName')}</label>
                    <input
                      value={senderNameDraft}
                      onChange={(e) => setSenderNameDraft(e.target.value)}
                      autoFocus
                      className="input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 uppercase tracking-widest mb-0.5">{t('pipeline.filterNumber')}</label>
                    <input
                      value={senderNumberDraft}
                      onChange={(e) => setSenderNumberDraft(e.target.value)}
                      placeholder={t('pipeline.detail.senderNumberPlaceholder')}
                      className="input w-full text-sm font-mono"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSaveSender} disabled={savingSender} className="btn btn-primary btn-sm text-xs flex items-center gap-1.5">
                    <FiSave size={12} /> {savingSender ? t('settings.saving') : t('settings.save')}
                  </button>
                  <button onClick={handleDiscardSender} disabled={savingSender} className="btn btn-ghost btn-sm text-xs">
                    {t('pipeline.detail.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <MetaField label={t('pipeline.filterName')} value={item.senderName} />
                <MetaField label={t('pipeline.filterNumber')} value={item.senderNumber && item.senderNumber !== 'unknown' ? `+${item.senderNumber}` : ''} />
              </div>
            )}
          </div>

          {item.publishedResult?.url && (
            <a href={item.publishedResult.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline">
              {t('pipeline.modal.viewOnYoutube')}
              <FiExternalLink size={12} />
            </a>
          )}

          {/* Text */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.col.text')}</p>
              {!editingText && (
                <button onClick={() => setEditingText(true)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                  <FiEdit3 size={11} /> {t('pipeline.detail.editText')}
                </button>
              )}
            </div>
            {editingText ? (
              <div className="space-y-2">
                <textarea
                  value={textDraft}
                  onChange={(e) => setTextDraft(e.target.value)}
                  rows={6}
                  autoFocus
                  className="input w-full text-sm resize-y whitespace-pre-wrap"
                />
                <div className="flex gap-2">
                  <button onClick={handleSaveText} disabled={savingText} className="btn btn-primary btn-sm text-xs flex items-center gap-1.5">
                    <FiSave size={12} /> {savingText ? t('settings.saving') : t('settings.save')}
                  </button>
                  <button onClick={handleDiscardText} disabled={savingText} className="btn btn-ghost btn-sm text-xs">
                    {t('pipeline.detail.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 whitespace-pre-wrap min-h-[2.5rem]">
                {item.text || <span className="italic text-gray-400">{t('pipeline.detail.noText')}</span>}
              </p>
            )}
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                {t('pipeline.col.attachments')} ({item.attachments?.length || 0})
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-xs text-primary-600 hover:underline flex items-center gap-1 disabled:opacity-50"
              >
                <FiPlus size={11} /> {uploading ? t('pipeline.detail.uploading') : t('pipeline.detail.addReplaceVideo')}
              </button>
              <input ref={fileInputRef} type="file" accept="video/*,image/*" className="hidden" onChange={handleFileChange} />
            </div>

            {item.attachments?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {item.attachments.map((att) => {
                  const Icon = attachmentIcon(att.type);
                  return (
                    <div key={att.filename} className="flex items-center gap-2.5 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                      <Icon size={16} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate flex-1">{att.filename}</span>
                      <a href={pipelineService.getAttachmentUrl(att.filename)} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-primary-600 flex-shrink-0">
                        <FiExternalLink size={13} />
                      </a>
                      <button
                        onClick={() => handleRemoveAttachment(att.filename)}
                        disabled={removingFilename === att.filename}
                        title={t('pipeline.detail.removeAttachment')}
                        className="text-gray-400 hover:text-danger-600 flex-shrink-0 disabled:opacity-40"
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-400">{t('pipeline.detail.noAttachments')}</p>
            )}
          </div>

          {/* Generated content — read-only preview; edited from the Execute modal */}
          {hasGeneratedContent && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('pipeline.detail.generatedContent')}</p>
              <MetaField label={t('pipeline.modal.videoTitle')} value={content.title} block />
              <MetaField label={t('pipeline.modal.videoDescription')} value={content.description} block />
              {content.hashtags?.length > 0 && <MetaField label={t('pipeline.modal.hashtags')} value={content.hashtags.join(' ')} block />}
              {content.videoTags?.length > 0 && <MetaField label={t('pipeline.modal.videoTags')} value={content.videoTags.join(', ')} block />}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex-shrink-0 flex justify-end">
          <button onClick={onClose} className="btn btn-primary btn-sm">{t('pipeline.modal.close')}</button>
        </div>
      </div>
    </div>
  );
};

const MetaField = ({ label, value, block }) => (
  <div>
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">{label}</p>
    <p className={`text-sm text-gray-700 ${block ? 'whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-lg px-3 py-2' : 'truncate'}`}>
      {value || '—'}
    </p>
  </div>
);
